import classnames from 'classnames'
import * as React from 'react'

import BarChart from './BarChart'
import { EmptyDataOverlayWrapper } from './EmptyDataOverlayWrapper'
import LineChart from './LineChart'
import { XAxis } from './XAxis'
import { CHART_HEIGHT, LABELS_WIDTH } from './constants'
import { LineStackedBarChartProps, View } from './types'
import { getChartData } from './utils'

export function LineStackedBarChart({
  data,
  yearKeys,
  chartView,
  lineChartTooltipSort,
  emptyMessage,
  isEmpty,
  isFullWidth = false,
  hasXAxis = false,
  isShowAll = false,
  isInView = true,
  hasBeenInView = true,
}: LineStackedBarChartProps) {
  const wrapperRef = React.useRef<HTMLDivElement>(null)
  const [barWidth, setBarWidth] = React.useState(0)
  const { chartData, visibleAreas } = React.useMemo(
    () => (hasBeenInView ? getChartData(data, yearKeys, chartView) : { chartData: [], visibleAreas: [] }),
    [data, yearKeys, hasBeenInView, chartView]
  )

  const columnsCount = yearKeys.length

  React.useEffect(() => {
    const wrapperWidth = wrapperRef.current?.clientWidth ?? 0
    setBarWidth((wrapperWidth - LABELS_WIDTH) / columnsCount)
  }, [columnsCount])

  return (
    <EmptyDataOverlayWrapper
      className={classnames({ 'pb-4.5': hasXAxis })}
      emptyMessage={emptyMessage}
      isEmpty={isEmpty}
    >
      <div className="relative flex w-full justify-end" style={{ height: CHART_HEIGHT }} ref={wrapperRef}>
        <div className="absolute left-0 right-8 top-0 border-t border-dashed border-secondary"></div>
        <div className="absolute left-0 right-8 top-1/4 border-t border-dashed border-secondary"></div>
        <div className="absolute left-0 right-8 top-1/2 border-t border-dashed border-secondary"></div>
        <div className="absolute left-0 right-8 top-3/4 border-t border-dashed border-secondary"></div>
        <div className="absolute bottom-0 left-0 right-8 border-t border-secondary"></div>

        {isInView && chartView === View.bars && (
          <BarChart
            chartData={chartData}
            visibleAreas={visibleAreas}
            columnsCount={columnsCount}
            isFullWidth={isFullWidth}
            barWidth={isFullWidth ? barWidth : undefined}
          />
        )}
        {isInView && chartView === View.lines && (
          <LineChart
            chartData={chartData}
            visibleAreas={visibleAreas}
            columnsCount={yearKeys.length}
            isFullWidth={isFullWidth}
            barWidth={isFullWidth ? barWidth : undefined}
            tooltipSort={lineChartTooltipSort}
            showTotal={isShowAll}
          />
        )}

        {hasXAxis && <XAxis barWidth={barWidth} chartView={chartView} yearKeys={yearKeys} />}
      </div>
    </EmptyDataOverlayWrapper>
  )
}
